
import { defineComponent, nextTick, ref } from "vue";
import { debounce } from "lodash";
import { HopDongService } from "@/core/services/HopDong.service";
import { useRouterCustom } from "@/core/hooks/use-router-custom";
import { swalNotification, getErrorMsg } from "@/core/helpers/utils";
import { IHopDongResponseBase } from "@/core/interfaces/ApiResponses";
import { useLoading } from "@/core/hooks/use-loading";
import { usePagination } from "@/core/hooks/use-pagination";
import { usePageHeader } from "@/core/hooks/usePageHeader";
import { HOP_DONG } from "@/core/constants/permissions";
import { faPrint } from "@fortawesome/free-solid-svg-icons";

import PageContentBase from "@/layout/_shared/PageContentBase.vue";
import PageHeaderFilters from "@/layout/header/PageHeaderFilters.vue";
import PageHeaderActions from "@/layout/header/PageHeaderActions.vue";
import PageHeaderActionItem from "@/layout/header/PageHeaderActionItem.vue";
import HopDongTable from "@/views/crafted/pages/hop-dong/hop-dong-table/HopDongTable.vue";
import Pagination from "@/components/shared/pagination/Pagination.vue";
import FilterItem from "@/layout/_shared/FilterItem.vue";

export default defineComponent({
  name: "danh-sach-hop-dong",

  components: {
    PageContentBase,
    PageHeaderFilters,
    PageHeaderActions,
    PageHeaderActionItem,
    HopDongTable,
    Pagination,
    FilterItem,
  },

  setup() {
    const { push } = useRouterCustom();
    const { startLoading, endLoading, isLoading } = useLoading();
    const { currentPage, totalItems, perPage, perPageArr } = usePagination();

    const hopDongType = ref<number>(-1);
    const hopDongItems = ref<IHopDongResponseBase[]>([]);
    document.title = "Hợp đồng - Đường sắt Hà Nội";

    const { searchQuery } = usePageHeader({
      title: "Danh sách hợp đồng",
      breadcrumbs: [{ text: "Hợp đồng" }],
      showSearch: true,
      searchPlaceholder: "Tìm theo mã hoặc tên",
      addLabel: "Thêm hợp đồng",
      addPermissions: [HOP_DONG.TAO_MOI],
      onAdd: () => push("/quan-ly/hop-dong/tao-moi"),
    });

    return {
      hopDongItems,
      push,
      startLoading,
      endLoading,
      isLoading,
      currentPage,
      totalItems,
      perPage,
      perPageArr,
      hopDongType,
      searchQuery,
      faPrint,
    };
  },

  data() {
    return {
      hopDongTypeOptions: [
        {
          value: -1,
          label: "Tất cả các loại",
        },
        {
          value: 1,
          label: "Mua bán",
        },
        {
          value: 2,
          label: "Điều chỉnh",
        },
      ],
    };
  },

  async mounted() {
    await this.fetchData();
  },

  watch: {
    async currentPage() {
      await this.fetchData();
    },

    async perPage() {
      if (this.currentPage === 1) {
        await this.fetchData();
        return;
      }
      this.currentPage = 1;
    },

    async hopDongType() {
      nextTick().then(async () => {
        if (this.currentPage === 1) {
          await this.fetchData();
          return;
        }
        this.currentPage = 1;
      });
    },

    searchQuery: debounce(function () {
      this.fetchData();
    }, 500),
  },

  methods: {
    openPrintViewer() {
      const route = this.$router.resolve({
        name: "xem-danh-sach-hop-dong",
        query: {
          ...(this.searchQuery ? { s: this.searchQuery } : {}),
          ...(this.hopDongType > 0 ? { loai: String(this.hopDongType) } : {}),
        },
      });

      window.open(route.href, "_blank");
    },

    async fetchData() {
      try {
        this.startLoading();

        const {
          data: {
            data: { data, total },
          },
        } = await HopDongService.fetchAllContracts({
          current_page: this.currentPage,
          per_page: this.perPage,
          searchTerm: this.searchQuery,
          hopDongType: this.hopDongType,
        });

        this.hopDongItems = data;
        this.totalItems = total;
        this.endLoading();
      } catch (error) {
        this.endLoading();

        await swalNotification(
          getErrorMsg(
            error,
            "Xin lỗi, có vẻ có lỗi xảy ra khi tải danh sách hợp đồng"
          ),
          "error"
        );
        await this.push("/trang-chu");
      }
    },
  },
});
